import React, { useCallback, useEffect, useState } from "react";
import { Button, Chip, CircularProgress, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import { mdiRefresh, mdiAlertCircle, mdiAlertOutline, mdiCheckCircle } from "@mdi/js";
import { BackofficeAdminServices, NotistackServices, QueueHealthResponse, QueueHealthSnapshot } from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

/**
 * EK-331 — Dashboard de saude das filas BullMQ.
 *
 * Mostra para cada fila (critical/default/background):
 *  - Contadores por estado (waiting/active/delayed/failed/completed)
 *  - Latencia p50/p95/p99 das ultimas 24h
 *  - Top 10 reasons de falha (DLQ analysis)
 *
 * Auto-refresh opcional (30s) + botao manual.
 */

const HEALTH_META: Record<QueueHealthSnapshot["healthStatus"], { color: string; label: string; icon: string }> = {
	ok: { color: "success", label: "OK", icon: mdiCheckCircle },
	warning: { color: "warning", label: "Atenção", icon: mdiAlertOutline },
	degraded: { color: "error", label: "Degradado", icon: mdiAlertCircle },
};

function fmtMs(ms: number): string {
	if (ms < 1000) return `${ms}ms`;
	if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
	return `${(ms / 60_000).toFixed(1)}min`;
}

function fmtRelative(epoch: number | null): string {
	if (!epoch) return "—";
	const diff = Date.now() - epoch;
	if (diff < 60_000) return "agora";
	if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m atrás`;
	if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h atrás`;
	return new Date(epoch).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

const QueueCard: React.FC<{ snapshot: QueueHealthSnapshot }> = ({ snapshot }) => {
	const meta = HEALTH_META[snapshot.healthStatus];
	const { counts, latency24h, dlqTopReasons } = snapshot;
	return (
		<div className={styles.queueCard}>
			<div className={styles.queueHeader}>
				<h3 className={styles.queueName}>{snapshot.queue}</h3>
				<Chip
					size="small"
					icon={<Icon path={meta.icon} size={0.7} />}
					label={meta.label}
					color={meta.color as any}
					variant="filled"
				/>
			</div>

			<div className={styles.statsRow}>
				<div className={styles.stat}>
					<span className={styles.statLabel}>Waiting</span>
					<span className={styles.statValue}>{counts.waiting ?? 0}</span>
				</div>
				<div className={styles.stat}>
					<span className={styles.statLabel}>Active</span>
					<span className={styles.statValue}>{counts.active ?? 0}</span>
				</div>
				<div className={styles.stat}>
					<span className={styles.statLabel}>Delayed</span>
					<span className={styles.statValue}>{counts.delayed ?? 0}</span>
				</div>
				<div className={styles.stat}>
					<span className={styles.statLabel}>Failed</span>
					<span className={`${styles.statValue} ${counts.failed > 0 ? styles.statValueError : ""}`}>{counts.failed ?? 0}</span>
				</div>
				<div className={styles.stat}>
					<span className={styles.statLabel}>Completed</span>
					<span className={styles.statValue}>{counts.completed ?? 0}</span>
				</div>
				{snapshot.stalledCount > 0 && (
					<div className={styles.stat}>
						<span className={styles.statLabel}>Stalled</span>
						<span className={`${styles.statValue} ${styles.statValueError}`}>{snapshot.stalledCount}</span>
					</div>
				)}
			</div>

			<div className={styles.section}>
				<h4 className={styles.sectionTitle}>Latência últimas 24h ({latency24h.sampleSize} amostras)</h4>
				{latency24h.sampleSize === 0 ? (
					<p className={styles.empty}>Sem jobs completos na janela.</p>
				) : (
					<div className={styles.latencyGrid}>
						<div className={styles.latencyCell}>
							<span className={styles.latencyLabel}>avg</span>
							<span className={styles.latencyValue}>{fmtMs(latency24h.avgMs)}</span>
						</div>
						<div className={styles.latencyCell}>
							<span className={styles.latencyLabel}>p50</span>
							<span className={styles.latencyValue}>{fmtMs(latency24h.p50Ms)}</span>
						</div>
						<div className={styles.latencyCell}>
							<span className={styles.latencyLabel}>p95</span>
							<span className={styles.latencyValue}>{fmtMs(latency24h.p95Ms)}</span>
						</div>
						<div className={styles.latencyCell}>
							<span className={styles.latencyLabel}>p99</span>
							<span className={styles.latencyValue}>{fmtMs(latency24h.p99Ms)}</span>
						</div>
						<div className={styles.latencyCell}>
							<span className={styles.latencyLabel}>max</span>
							<span className={styles.latencyValue}>{fmtMs(latency24h.maxMs)}</span>
						</div>
					</div>
				)}
			</div>

			<div className={styles.section}>
				<h4 className={styles.sectionTitle}>Top reasons de falha (DLQ analysis)</h4>
				{dlqTopReasons.length === 0 ? (
					<p className={styles.empty}>Nenhuma falha recente.</p>
				) : (
					<table className={styles.dlqTable}>
						<thead>
							<tr>
								<th>#</th>
								<th>Reason (normalizada)</th>
								<th>Count</th>
								<th>Última</th>
							</tr>
						</thead>
						<tbody>
							{dlqTopReasons.map((r, idx) => (
								<tr key={idx}>
									<td className={styles.idx}>{idx + 1}</td>
									<td>
										<Tooltip title={r.sample ?? r.reasonNormalized} placement="top-start">
											<span className={styles.reasonText}>{r.reasonNormalized}</span>
										</Tooltip>
									</td>
									<td className={styles.count}>{r.count}</td>
									<td className={styles.lastSeen}>{fmtRelative(r.lastSeen)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
};

export const QueueHealthPage: React.FC = () => {
	const [data, setData] = useState<QueueHealthResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [autoRefresh, setAutoRefresh] = useState(true);

	const fetchData = useCallback(async () => {
		try {
			const response = await BackofficeAdminServices.getQueueHealth();
			setData(response);
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar saúde das filas");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchData();
	}, [fetchData]);

	useEffect(() => {
		if (!autoRefresh) return;
		const interval = setInterval(() => void fetchData(), 30_000);
		return () => clearInterval(interval);
	}, [autoRefresh, fetchData]);

	if (loading && !data) {
		return (
			<div className={styles.page}>
				<div className={styles.loadingState}>
					<CircularProgress size={32} />
				</div>
			</div>
		);
	}

	if (!data) {
		return (
			<div className={styles.page}>
				<p>Sem dados disponíveis.</p>
			</div>
		);
	}

	const overallMeta = HEALTH_META[data.overallHealth];

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<div>
					<h1 className={styles.title}>Saúde das Filas (BullMQ)</h1>
					<p className={styles.subtitle}>
						Ambiente: <strong>{data.environment}</strong> · Atualizado: {new Date(data.timestamp).toLocaleTimeString("pt-BR")} ·{" "}
						<Chip
							size="small"
							icon={<Icon path={overallMeta.icon} size={0.7} />}
							label={`Status geral: ${overallMeta.label}`}
							color={overallMeta.color as any}
							variant="outlined"
						/>
					</p>
				</div>
				<div className={styles.actions}>
					<Button
						size="small"
						variant={autoRefresh ? "contained" : "outlined"}
						onClick={() => setAutoRefresh((v) => !v)}
					>
						{autoRefresh ? "Auto-refresh: ON" : "Auto-refresh: OFF"}
					</Button>
					<Button
						size="small"
						variant="outlined"
						onClick={() => void fetchData()}
						disabled={loading}
						startIcon={<Icon path={mdiRefresh} size={0.8} />}
					>
						Recarregar
					</Button>
				</div>
			</div>

			<div className={styles.queuesGrid}>
				{data.queues.map((snapshot) => (
					<QueueCard key={snapshot.queue} snapshot={snapshot} />
				))}
			</div>
		</div>
	);
};
